import React from "react";

export default function HouseMaidHealthCheckupForm({
  formData,
  onChange,
  isViewOnly = false,
  editingFormId,
  shouldDisableField,
  getFieldStyle
}) {
  const handleChange = (field, value) => {
    if (!isViewOnly) { // Only allow changes if not in view-only mode
      onChange(field, value);
    }
  };

  const isDisabled = (fieldName, fieldValue) => {
    if (shouldDisableField) return shouldDisableField("houseMaidHealthCheckup", fieldName, fieldValue);
    if (isViewOnly) return true;
    const patientInfoFields = ["patientName", "mobileNumber", "civilId"];
    return patientInfoFields.includes(fieldName);
  };

  const getStyle = (fieldName, fieldValue) => {
    if (getFieldStyle) return getFieldStyle("houseMaidHealthCheckup", fieldName, fieldValue);
    if (isDisabled(fieldName, fieldValue) && !isViewOnly) {
      return { backgroundColor: "#e9ecef", cursor: "not-allowed" };
    }
    return {};
  };

  return (
    <fieldset disabled={isViewOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
      <div className="row g-3">
        {/* Patient Information Section */}
        <div className="col-12">
          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Patient Name:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.patientName}
                onChange={(e) => handleChange("patientName", e.target.value)}
                disabled={isDisabled("patientName", formData.patientName)}
                style={getStyle("patientName", formData.patientName)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Visit Date:</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={formData.visitDate || ''}
                onChange={(e) => handleChange("visitDate", e.target.value)}
                disabled={isDisabled("visitDate", formData.visitDate)}
                style={getStyle("visitDate", formData.visitDate)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">ID#:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.idNumber || ''}
                onChange={(e) => handleChange("idNumber", e.target.value)}
                disabled={isDisabled("idNumber", formData.idNumber)}
                style={getStyle("idNumber", formData.idNumber)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Mobile#:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.mobileNumber}
                onChange={(e) => handleChange("mobileNumber", e.target.value)}
                disabled={isDisabled("mobileNumber", formData.mobileNumber)}
                style={getStyle("mobileNumber", formData.mobileNumber)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Ref#:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.refNumber || ''}
                onChange={(e) => handleChange("refNumber", e.target.value)}
                disabled={isDisabled("refNumber", formData.refNumber)}
                style={getStyle("refNumber", formData.refNumber)}
              />
            </div>
            <div className="col-md-1">
              <label className="form-label fw-semibold">CPR#:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.civilId}
                onChange={(e) => handleChange("civilId", e.target.value)}
                disabled={isDisabled("civilId", formData.civilId)}
                style={getStyle("civilId", formData.civilId)}
              />
            </div>
          </div>
          <div className="row g-2 mt-1">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Doctor Name:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.doctorName}
                onChange={(e) => handleChange("doctorName", e.target.value)}
                disabled={isDisabled("doctorName", formData.doctorName)}
                style={getStyle("doctorName", formData.doctorName)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">Department:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.department}
                onChange={(e) => handleChange("department", e.target.value)}
                disabled={isDisabled("department", formData.department)}
                style={getStyle("department", formData.department)}
              />
            </div>
          </div>
        </div>

        {/* VITALS Section */}
        <div className="col-12 mt-3">
          <h6 className="text-center bg-secondary text-white py-2 mb-2">VITALS</h6>
          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Blood Pressure (mmHg)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="e.g., 120/80"
                value={formData.bloodPressure}
                onChange={(e) => handleChange("bloodPressure", e.target.value)}
                disabled={isDisabled("bloodPressure", formData.bloodPressure)}
                style={getStyle("bloodPressure", formData.bloodPressure)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Pulse</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.pulse}
                onChange={(e) => handleChange("pulse", e.target.value)}
                disabled={isDisabled("pulse", formData.pulse)}
                style={getStyle("pulse", formData.pulse)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Height (cm)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.heightCm}
                onChange={(e) => handleChange("heightCm", e.target.value)}
                disabled={isDisabled("heightCm", formData.heightCm)}
                style={getStyle("heightCm", formData.heightCm)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Weight (kg)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.weight}
                onChange={(e) => handleChange("weight", e.target.value)}
                disabled={isDisabled("weight", formData.weight)}
                style={getStyle("weight", formData.weight)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">BMI (kg/m²)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.bmi}
                onChange={(e) => handleChange("bmi", e.target.value)}
                disabled={isDisabled("bmi", formData.bmi)}
                style={getStyle("bmi", formData.bmi)}
              />
            </div>
          </div>
        </div>

        {/* VISION TEST Section */}
        <div className="col-12 mt-3">
          <h6 className="text-center bg-secondary text-white py-2 mb-2">VISION TEST</h6>

          {/* Table Header */}
          <div className="row g-2 mb-2">
            <div className="col-md-3">
              <label className="form-label fw-bold">Particulars</label>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">Readings</label>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold">Normal/Abnormal/Corrected</label>
            </div>
          </div>

          {/* Right Eye Row */}
          <div className="row g-2 mb-2 align-items-center">
            <div className="col-md-3">
              <label className="form-label mb-0">Right Eye</label>
            </div>
            <div className="col-md-3">
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.rightEyeReadings || ''}
                onChange={(e) => handleChange("rightEyeReadings", e.target.value)}
                placeholder="Enter reading"
              />
            </div>
            <div className="col-md-6">
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="rightEyeStatus"
                    id="rightEyeNormal"
                    checked={formData.rightEyeStatus === "normal"}
                    onChange={(e) => handleChange("rightEyeStatus", "normal")}
                  />
                  <label className="form-check-label" htmlFor="rightEyeNormal">
                    Normal
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="rightEyeStatus"
                    id="rightEyeAbnormal"
                    checked={formData.rightEyeStatus === "abnormal"}
                    onChange={(e) => handleChange("rightEyeStatus", "abnormal")}
                  />
                  <label className="form-check-label" htmlFor="rightEyeAbnormal">
                    Abnormal
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="rightEyeStatus"
                    id="rightEyeCorrected"
                    checked={formData.rightEyeStatus === "corrected"}
                    onChange={(e) => handleChange("rightEyeStatus", "corrected")}
                  />
                  <label className="form-check-label" htmlFor="rightEyeCorrected">
                    Corrected
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Left Eye Row */}
          <div className="row g-2 mb-2 align-items-center">
            <div className="col-md-3">
              <label className="form-label mb-0">Left Eye</label>
            </div>
            <div className="col-md-3">
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.leftEyeReadings || ''}
                onChange={(e) => handleChange("leftEyeReadings", e.target.value)}
                placeholder="Enter reading"
              />
            </div>
            <div className="col-md-6">
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="leftEyeStatus"
                    id="leftEyeNormal"
                    checked={formData.leftEyeStatus === "normal"}
                    onChange={(e) => handleChange("leftEyeStatus", "normal")}
                  />
                  <label className="form-check-label" htmlFor="leftEyeNormal">
                    Normal
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="leftEyeStatus"
                    id="leftEyeAbnormal"
                    checked={formData.leftEyeStatus === "abnormal"}
                    onChange={(e) => handleChange("leftEyeStatus", "abnormal")}
                  />
                  <label className="form-check-label" htmlFor="leftEyeAbnormal">
                    Abnormal
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="leftEyeStatus"
                    id="leftEyeCorrected"
                    checked={formData.leftEyeStatus === "corrected"}
                    onChange={(e) => handleChange("leftEyeStatus", "corrected")}
                  />
                  <label className="form-check-label" htmlFor="leftEyeCorrected">
                    Corrected
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Color Vision Row */}
          <div className="row g-2 mb-2 align-items-center">
            <div className="col-md-3">
              <label className="form-label mb-0">Color Vision</label>
            </div>
            <div className="col-md-3">
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.colorVisionReadings || ''}
                onChange={(e) => handleChange("colorVisionReadings", e.target.value)}
                placeholder="Enter reading"
              />
            </div>
            <div className="col-md-6">
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="colorVisionStatus"
                    id="colorVisionNormal"
                    checked={formData.colorVisionStatus === "normal"}
                    onChange={(e) => handleChange("colorVisionStatus", "normal")}
                  />
                  <label className="form-check-label" htmlFor="colorVisionNormal">
                    Normal
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="colorVisionStatus"
                    id="colorVisionAbnormal"
                    checked={formData.colorVisionStatus === "abnormal"}
                    onChange={(e) => handleChange("colorVisionStatus", "abnormal")}
                  />
                  <label className="form-check-label" htmlFor="colorVisionAbnormal">
                    Abnormal
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="colorVisionStatus"
                    id="colorVisionCorrected"
                    checked={formData.colorVisionStatus === "corrected"}
                    onChange={(e) => handleChange("colorVisionStatus", "corrected")}
                  />
                  <label className="form-check-label" htmlFor="colorVisionCorrected">
                    Corrected
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Comments if Any */}
          <div className="row g-2 mt-2">
            <div className="col-md-3">
              <label className="form-label mb-0">Comments if Any:</label>
            </div>
            <div className="col-md-9">
              <textarea
                className="form-control form-control-sm"
                rows="2"
                value={formData.eyeComment || ''}
                onChange={(e) => handleChange("eyeComment", e.target.value)}
                placeholder="Enter any comments"
              ></textarea>
            </div>
          </div>
        </div>

        {/* CLINICAL EXAMINATION Section */}
        <div className="col-12 mt-3">
          <h6 className="text-center bg-secondary text-white py-2 mb-2">CLINICAL EXAMINATION</h6>

          {/* Cardiovascular Examination */}
          <div className="mb-3">
            <label className="form-label fw-bold mb-2">Cardiovascular Examination</label>

            {/* General Appearance */}
            <div className="row g-2 mb-2 align-items-center">
              <div className="col-md-3">
                <label className="form-label mb-0">General Appearance</label>
              </div>
              <div className="col-md-3">
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="generalAppearanceStatus"
                      id="genAppNormal"
                      checked={formData.generalAppearanceStatus === "normal"}
                      onChange={(e) => handleChange("generalAppearanceStatus", "normal")}
                    />
                    <label className="form-check-label" htmlFor="genAppNormal">
                      Normal
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="generalAppearanceStatus"
                      id="genAppAbnormal"
                      checked={formData.generalAppearanceStatus === "abnormal"}
                      onChange={(e) => handleChange("generalAppearanceStatus", "abnormal")}
                    />
                    <label className="form-check-label" htmlFor="genAppAbnormal">
                      Abnormal
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formData.generalAppearanceRemarks || ''}
                  onChange={(e) => handleChange("generalAppearanceRemarks", e.target.value)}
                  placeholder="Remarks"
                />
              </div>
            </div>

            {/* Auscultation (Cardiovascular) */}
            <div className="row g-2 mb-2 align-items-center">
              <div className="col-md-3">
                <label className="form-label mb-0">Auscultation</label>
              </div>
              <div className="col-md-3">
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="cardioAuscultationStatus"
                      id="cardioAusNormal"
                      checked={formData.cardioAuscultationStatus === "normal"}
                      onChange={(e) => handleChange("cardioAuscultationStatus", "normal")}
                    />
                    <label className="form-check-label" htmlFor="cardioAusNormal">
                      Normal
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="cardioAuscultationStatus"
                      id="cardioAusAbnormal"
                      checked={formData.cardioAuscultationStatus === "abnormal"}
                      onChange={(e) => handleChange("cardioAuscultationStatus", "abnormal")}
                    />
                    <label className="form-check-label" htmlFor="cardioAusAbnormal">
                      Abnormal
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formData.cardioAuscultationRemarks || ''}
                  onChange={(e) => handleChange("cardioAuscultationRemarks", e.target.value)}
                  placeholder="Remarks"
                />
              </div>
            </div>
          </div>

          {/* Respiratory Examination */}
          <div className="mb-3">
            <label className="form-label fw-bold mb-2">Respiratory Examination</label>

            {/* Auscultation (Respiratory) */}
            <div className="row g-2 mb-2 align-items-center">
              <div className="col-md-3">
                <label className="form-label mb-0">Auscultation</label>
              </div>
              <div className="col-md-3">
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="respAuscultationStatus"
                      id="respAusNormal"
                      checked={formData.respAuscultationStatus === "normal"}
                      onChange={(e) => handleChange("respAuscultationStatus", "normal")}
                    />
                    <label className="form-check-label" htmlFor="respAusNormal">
                      Normal
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="respAuscultationStatus"
                      id="respAusAbnormal"
                      checked={formData.respAuscultationStatus === "abnormal"}
                      onChange={(e) => handleChange("respAuscultationStatus", "abnormal")}
                    />
                    <label className="form-check-label" htmlFor="respAusAbnormal">
                      Abnormal
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formData.respAuscultationRemarks || ''}
                  onChange={(e) => handleChange("respAuscultationRemarks", e.target.value)}
                  placeholder="Remarks"
                />
              </div>
            </div>

            {/* Chest X-ray */}
            <div className="row g-2 mb-2 align-items-center">
              <div className="col-md-3">
                <label className="form-label mb-0">Chest X-ray</label>
              </div>
              <div className="col-md-3">
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="chestXrayStatus"
                      id="chestXrayNormal"
                      checked={formData.chestXrayStatus === "normal"}
                      onChange={(e) => handleChange("chestXrayStatus", "normal")}
                    />
                    <label className="form-check-label" htmlFor="chestXrayNormal">
                      Normal
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="chestXrayStatus"
                      id="chestXrayAbnormal"
                      checked={formData.chestXrayStatus === "abnormal"}
                      onChange={(e) => handleChange("chestXrayStatus", "abnormal")}
                    />
                    <label className="form-check-label" htmlFor="chestXrayAbnormal">
                      Abnormal
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formData.chestXrayRemarks || ''}
                  onChange={(e) => handleChange("chestXrayRemarks", e.target.value)}
                  placeholder="Remarks"
                />
              </div>
            </div>
          </div>
        </div>
        {/* LABORATORY EXAMINATION Section */}
        <div className="col-12 mt-3">
          <h6 className="text-center bg-secondary text-white py-2 mb-2">LABORATORY EXAMINATION</h6>

          {/* Serology */}
          <div className="row g-2 mb-3">
            <div className="col-12">
              <label className="form-label fw-bold">Serology</label>
            </div>

            {/* Header Row */}
            <div className="col-md-2">
              <label className="form-label small fw-semibold">Test</label>
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-semibold">Positive/Negative</label>
            </div>
            <div className="col-md-7">
              <label className="form-label small fw-semibold">Comments</label>
            </div>

            {/* HIV Row */}
            <div className="col-md-2">
              <label className="form-label mb-0">HIV</label>
            </div>
            <div className="col-md-3">
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="hivResult"
                    id="hivPositive"
                    checked={formData.hivResult === "positive"}
                    onChange={(e) => handleChange("hivResult", "positive")}
                  />
                  <label className="form-check-label" htmlFor="hivPositive">
                    Positive
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="hivResult"
                    id="hivNegative"
                    checked={formData.hivResult === "negative"}
                    onChange={(e) => handleChange("hivResult", "negative")}
                  />
                  <label className="form-check-label" htmlFor="hivNegative">
                    Negative
                  </label>
                </div>
              </div>
            </div>
            <div className="col-md-7">
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.hivComments || ''}
                onChange={(e) => handleChange("hivComments", e.target.value)}
                placeholder="Enter comments"
              />
            </div>

            {/* HBsAg Row */}
            <div className="col-md-2">
              <label className="form-label mb-0">HBsAg</label>
            </div>
            <div className="col-md-3">
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="hbsagResult"
                    id="hbsagPositive"
                    checked={formData.hbsagResult === "positive"}
                    onChange={(e) => handleChange("hbsagResult", "positive")}
                  />
                  <label className="form-check-label" htmlFor="hbsagPositive">
                    Positive
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="hbsagResult"
                    id="hbsagNegative"
                    checked={formData.hbsagResult === "negative"}
                    onChange={(e) => handleChange("hbsagResult", "negative")}
                  />
                  <label className="form-check-label" htmlFor="hbsagNegative">
                    Negative
                  </label>
                </div>
              </div>
            </div>
            <div className="col-md-7">
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.hbsagComments || ''}
                onChange={(e) => handleChange("hbsagComments", e.target.value)}
                placeholder="Enter comments"
              />
            </div>

            {/* HCV Row */}
            <div className="col-md-2">
              <label className="form-label mb-0">HCV</label>
            </div>
            <div className="col-md-3">
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="hcvResult"
                    id="hcvPositive"
                    checked={formData.hcvResult === "positive"}
                    onChange={(e) => handleChange("hcvResult", "positive")}
                  />
                  <label className="form-check-label" htmlFor="hcvPositive">
                    Positive
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="hcvResult"
                    id="hcvNegative"
                    checked={formData.hcvResult === "negative"}
                    onChange={(e) => handleChange("hcvResult", "negative")}
                  />
                  <label className="form-check-label" htmlFor="hcvNegative">
                    Negative
                  </label>
                </div>
              </div>
            </div>
            <div className="col-md-7">
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.hcvComments || ''}
                onChange={(e) => handleChange("hcvComments", e.target.value)}
                placeholder="Enter comments"
              />
            </div>

            {/* VDRL Row */}
            <div className="col-md-2">
              <label className="form-label mb-0">VDRL</label>
            </div>
            <div className="col-md-3">
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="vdrlResult"
                    id="vdrlPositive"
                    checked={formData.vdrlResult === "positive"}
                    onChange={(e) => handleChange("vdrlResult", "positive")}
                  />
                  <label className="form-check-label" htmlFor="vdrlPositive">
                    Positive
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="vdrlResult"
                    id="vdrlNegative"
                    checked={formData.vdrlResult === "negative"}
                    onChange={(e) => handleChange("vdrlResult", "negative")}
                  />
                  <label className="form-check-label" htmlFor="vdrlNegative">
                    Negative
                  </label>
                </div>
              </div>
            </div>
            <div className="col-md-7">
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.vdrlComments || ''}
                onChange={(e) => handleChange("vdrlComments", e.target.value)}
                placeholder="Enter comments"
              />
            </div>
          </div>

          {/* Stool */}
          <div className="row g-2 mb-3">
            <div className="col-12">
              <label className="form-label fw-bold">Stool</label>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Ova & Parasites</label>
            </div>
            <div className="col-md-3">
              {/* <label className="form-label small fw-semibold">Present/Absent</label> */}
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="ovaParasitesStatus"
                    id="ovaPresent"
                    checked={formData.ovaParasitesStatus === "present"}
                    onChange={(e) => handleChange("ovaParasitesStatus", "present")}
                  />
                  <label className="form-check-label" htmlFor="ovaPresent">
                    Present
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="ovaParasitesStatus"
                    id="ovaAbsent"
                    checked={formData.ovaParasitesStatus === "absent"}
                    onChange={(e) => handleChange("ovaParasitesStatus", "absent")}
                  />
                  <label className="form-check-label" htmlFor="ovaAbsent">
                    Absent
                  </label>
                </div>
              </div>
            </div>
            <div className="col-md-7">
              <label className="form-label small">

              </label>
              {formData.ovaParasitesStatus === "present" && (
                <div className="d-flex flex-column gap-2">
                  <textarea
                    className="form-control form-control-sm"
                    rows="2"
                    value={formData.ovaParasitesComments || ""}
                    onChange={(e) =>
                      handleChange("ovaParasitesComments", e.target.value)
                    }
                    placeholder="Enter Comments"
                  ></textarea>
                  <textarea
                    className="form-control form-control-sm"
                    rows="2"
                    value={formData.ovaParasitesTreatments || ""}
                    onChange={(e) =>
                      handleChange("ovaParasitesTreatments", e.target.value)
                    }
                    placeholder="Enter Treatments"
                  ></textarea>
                </div>
              )}
            </div>
          </div>

          {/* Urine */}
          <div className="row g-2 mb-3">
            <div className="col-12">
              <label className="form-label fw-bold">Urine</label>
            </div>

            {/* Header Row */}
            <div className="col-md-2">
              <label className="form-label small fw-semibold">Test</label>
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-semibold">Result</label>
            </div>
            <div className="col-md-7">
              <label className="form-label small fw-semibold">Comments</label>
            </div>

            {/* Sugar Row */}
            <div className="col-md-2">
              <label className="form-label mb-0">Sugar</label>
            </div>
            <div className="col-md-3">
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="urineSugarStatus"
                    id="urineSugarPresent"
                    checked={formData.urineSugarStatus === "present"}
                    onChange={(e) => handleChange("urineSugarStatus", "present")}
                  />
                  <label className="form-check-label" htmlFor="urineSugarPresent">
                    Present
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="urineSugarStatus"
                    id="urineSugarAbsent"
                    checked={formData.urineSugarStatus === "absent"}
                    onChange={(e) => handleChange("urineSugarStatus", "absent")}
                  />
                  <label className="form-check-label" htmlFor="urineSugarAbsent">
                    Absent
                  </label>
                </div>
              </div>
            </div>
            <div className="col-md-7">
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.urineSugarComments || ''}
                onChange={(e) => handleChange("urineSugarComments", e.target.value)}
                placeholder="Enter comments"
              />
            </div>

            {/* Albumin Row */}
            <div className="col-md-2">
              <label className="form-label mb-0">Albumin</label>
            </div>
            <div className="col-md-3">
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="urineAlbuminStatus"
                    id="urineAlbuminPresent"
                    checked={formData.urineAlbuminStatus === "present"}
                    onChange={(e) => handleChange("urineAlbuminStatus", "present")}
                  />
                  <label className="form-check-label" htmlFor="urineAlbuminPresent">
                    Present
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="urineAlbuminStatus"
                    id="urineAlbuminAbsent"
                    checked={formData.urineAlbuminStatus === "absent"}
                    onChange={(e) => handleChange("urineAlbuminStatus", "absent")}
                  />
                  <label className="form-check-label" htmlFor="urineAlbuminAbsent">
                    Absent
                  </label>
                </div>
              </div>
            </div>
            <div className="col-md-7">
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.urineAlbuminComments || ''}
                onChange={(e) => handleChange("urineAlbuminComments", e.target.value)}
                placeholder="Enter comments"
              />
            </div>

            {/* Blood Row */}
            <div className="col-md-2">
              <label className="form-label mb-0">Blood</label>
            </div>
            <div className="col-md-3">
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="urineBloodStatus"
                    id="urineBloodPresent"
                    checked={formData.urineBloodStatus === "present"}
                    onChange={(e) => handleChange("urineBloodStatus", "present")}
                  />
                  <label className="form-check-label" htmlFor="urineBloodPresent">
                    Present
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="urineBloodStatus"
                    id="urineBloodAbsent"
                    checked={formData.urineBloodStatus === "absent"}
                    onChange={(e) => handleChange("urineBloodStatus", "absent")}
                  />
                  <label className="form-check-label" htmlFor="urineBloodAbsent">
                    Absent
                  </label>
                </div>
              </div>
            </div>
            <div className="col-md-7">
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.urineBloodComments || ''}
                onChange={(e) => handleChange("urineBloodComments", e.target.value)}
                placeholder="Enter comments"
              />
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div className="col-12 mt-3">
          <label className="form-label fw-bold">Notes (if any)</label>
          <textarea
            className="form-control form-control-sm"
            rows="3"
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
          ></textarea>
        </div>

        {/* Result and Doctor Section */}
        <div className="col-12 mt-3">
          <div className="row g-2">
            <div className="col-md-6">
              <label className="form-label fw-bold">Result: Fit / UnFit</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.result || ''}
                onChange={(e) => handleChange("result", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold">Doctor:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formData.examinerName}
                onChange={(e) => handleChange("examinerName", e.target.value)}
              />
            </div>
          </div>
          <div className="row g-2 mt-2">
            <div className="col-md-6">
              <label className="form-label fw-bold">Date + Time:</label>
              <input
                type="datetime-local"
                className="form-control form-control-sm"
                value={formData.examinerDateTime}
                onChange={(e) => handleChange("examinerDateTime", e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </fieldset>
  );
}
